import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auctionService } from "@/services";
import type { Auction } from "@/services/types";
import { PageHeader, BreadcrumbItem } from "@/components/feature/PageHeader";
import { Card } from "@/components/base/Card";
import { Button } from "@/components/base/Button";
import { Alert } from "@/components/base/Alert";
import { ErrorState } from "@/components/base/ErrorState";
import { LiveAuctionConsole } from "./components/LiveAuctionConsole";
import { ReservedCyclePanel } from "./components/ReservedCyclePanel";
import { ClosedAuctionView } from "./components/ClosedAuctionView";

export default function AuctionConsolePage() {
  const { groupId, cycle } = useParams<{ groupId: string; cycle: string }>();
  const navigate = useNavigate();
  const [auction, setAuction] = useState<Auction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!groupId || !cycle) return;
    setError(null);
    try {
      setAuction(await auctionService.getAuction(groupId, Number(cycle)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load this auction.");
      setAuction(null);
    }
  }, [groupId, cycle]);

  useEffect(() => {
    load();
  }, [load]);

  const back = (
    <Button variant="outline" size="sm" icon={<i className="ri-arrow-left-line" />} onClick={() => navigate("/auction")}>
      Back to dashboard
    </Button>
  );

  const breadcrumb = auction ? (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Auction" to="/auction" />
      <BreadcrumbItem label={`${auction.groupName} · Cycle ${auction.cycle}`} isLast />
    </>
  ) : (
    <>
      <BreadcrumbItem label="Home" to="/" />
      <BreadcrumbItem label="Auction" to="/auction" />
      <BreadcrumbItem label="Auction" isLast />
    </>
  );

  if (error) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Auction" actions={back} />
        <Card>
          <ErrorState message={error} onRetry={load} />
        </Card>
      </div>
    );
  }

  if (!auction) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title="Loading auction…" actions={back} />
        <div className="space-y-3">
          <div className="h-40 animate-pulse rounded-lg bg-background-200" />
          <div className="h-56 animate-pulse rounded-lg bg-background-200" />
        </div>
      </div>
    );
  }

  // Closed cycles are read-only audit records.
  if (auction.status === "closed") {
    return (
      <div>
        <PageHeader
          breadcrumb={breadcrumb}
          title={`${auction.groupName} · Cycle ${auction.cycle}`}
          subtitle={`Closed auction · ${auction.periodLabel}`}
          actions={back}
        />
        <ClosedAuctionView auction={auction} />
      </div>
    );
  }

  // Cycles must close in order.
  if (auction.blockedOutOfOrder) {
    return (
      <div>
        <PageHeader breadcrumb={breadcrumb} title={`${auction.groupName} · Cycle ${auction.cycle}`} actions={back} />
        <Card>
          <Alert variant="warning" title="This auction cannot be opened yet">
            An earlier cycle in this group is still unresolved. Cycles must close in order, so this auction is locked
            until the previous cycle is decided. Return to the dashboard and close the outstanding cycle first.
          </Alert>
          <div className="mt-4">
            <Button variant="outline" onClick={() => navigate("/auction")}>
              Back to dashboard
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  // Reserved organizer cycle — a separate, simpler screen with no bid interface.
  if (auction.isOrganizerReserved) {
    return (
      <div>
        <PageHeader
          breadcrumb={breadcrumb}
          title={`${auction.groupName} · Cycle ${auction.cycle}`}
          subtitle={`Reserved organizer cycle · ${auction.periodLabel}`}
          actions={back}
        />
        <ReservedCyclePanel auction={auction} onClosed={load} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        breadcrumb={breadcrumb}
        title={`${auction.groupName} · Cycle ${auction.cycle}`}
        subtitle={`Live auction · ${auction.periodLabel}`}
        actions={back}
      />
      <LiveAuctionConsole auction={auction} onClosed={load} />
    </div>
  );
}